import { Apple, Play } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { isNative } from '@/shared/api/platform'
import { trackEvent } from '@/shared/lib/analytics'
import { APP_STORE_URL, GOOGLE_PLAY_AVAILABLE, GOOGLE_PLAY_URL } from '@/shared/lib/appStores'
import { cn } from '@/shared/lib/cn'

export type AppStorePlacement = 'footer' | 'landing'

interface AppStoreBadgesProps {
  /** Dónde se pinta: va como `placement` en la analítica. */
  placement: AppStorePlacement
  /** `dark` = insignia clara sobre el pie negro; `light` = insignia negra sobre fondo claro. */
  variant?: 'dark' | 'light'
  /** `sm` = compactas, una al lado de otra (pie de página); `md` = las de la portada. */
  size?: 'sm' | 'md'
  /** Para que la propia `<GooglePlayBadge>` sepa si enlazar (por defecto, la constante). */
  googlePlayAvailable?: boolean
  className?: string
}

/**
 * Insignias «App Store» y «Google Play» con la estética brutal de la marca (spec
 * 2026-10-01-descarga-la-app). No se pintan en la app nativa: ya la tienen.
 */
export function AppStoreBadges({
  placement,
  variant = 'light',
  size = 'md',
  googlePlayAvailable = GOOGLE_PLAY_AVAILABLE,
  className,
}: AppStoreBadgesProps) {
  const { t } = useTranslation()
  if (isNative()) return null

  const sm = size === 'sm'
  const box = sm ? 'gap-2 rounded-lg px-3 py-1.5' : 'gap-3 rounded-xl px-4 py-2'
  const iconSize = sm ? 18 : 26
  const tone =
    variant === 'dark'
      ? 'border-background bg-background text-foreground'
      : 'border-foreground bg-foreground text-background'

  return (
    <div className={cn('flex flex-wrap', sm ? 'gap-2' : 'gap-3', className)}>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('appStores.appStoreAria')}
        onClick={() => trackEvent('App Download Click', { store: 'app_store', placement })}
        className={cn('brutal-sm brutal-press inline-flex items-center border-2', box, tone)}
      >
        <BadgeContent
          icon={<Apple size={iconSize} strokeWidth={2.25} aria-hidden="true" />}
          kicker={t('appStores.downloadOn')}
          name="App Store"
          small={sm}
        />
      </a>
      {googlePlayAvailable ? (
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('appStores.googlePlayAria')}
          onClick={() => trackEvent('App Download Click', { store: 'google_play', placement })}
          className={cn('brutal-sm brutal-press inline-flex items-center border-2', box, tone)}
        >
          <BadgeContent
            icon={<Play size={iconSize - 2} strokeWidth={2.5} aria-hidden="true" />}
            kicker={t('appStores.getItOn')}
            name="Google Play"
            small={sm}
          />
        </a>
      ) : (
        <span
          role="img"
          aria-label={t('appStores.googlePlaySoonAria')}
          className={cn(
            'inline-flex items-center border-2 border-dashed opacity-80',
            box,
            variant === 'dark'
              ? 'border-background/70 text-background'
              : 'border-foreground text-foreground',
          )}
        >
          <BadgeContent
            icon={<Play size={iconSize - 2} strokeWidth={2.5} aria-hidden="true" />}
            kicker={t('appStores.comingSoon')}
            name="Google Play"
            small={sm}
          />
        </span>
      )}
    </div>
  )
}

function BadgeContent({
  icon,
  kicker,
  name,
  small,
}: {
  icon: ReactNode
  kicker: string
  name: string
  small: boolean
}) {
  return (
    <>
      {icon}
      <span className="flex flex-col text-left leading-tight" aria-hidden="true">
        <span
          className={cn(
            'font-semibold uppercase tracking-wide opacity-80',
            small ? 'text-[9px]' : 'text-[11px]',
          )}
        >
          {kicker}
        </span>
        <span className={cn('font-display font-bold', small ? 'text-sm' : 'text-lg')}>{name}</span>
      </span>
    </>
  )
}
